/* disable-eslint */
import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { UserContext } from "context/user";
import {
  getContactDetails,
  createContactDetails,
  updateContactDetails,
} from "api/User";
import appLogo from "assets/logo/logo-only.png";
import Button from "components/Button";
import { verifyEmail } from "api/Auth";
import CreateNewFarmContainer from "layouts/CreateNewFarmButton";
import ModalContainer from "../components/ModalContainer";
import AddPaymentMethod from "../components/AddPaymentMethod";
import CompleteProfile from "../components/CompleteProfile";
import VerifyEmail from "../components/VerifyEmail";
import "./index.scss";

const GettingStartedContainer = () => {
  const history = useHistory();
  const { token, user } = useContext(UserContext);
  const [openModal, setOpenModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [selectedContent, setSelectedContent] = useState("");
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    userType: "",
  });
  const [formType, setFormType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;

    const userDetailsData = await getContactDetails(token);
    setUserDetails(userDetailsData.data);
  }, [token]);

  useEffect(() => {
    if (userDetails) {
      setFormValues({
        firstName: userDetails?.first_name || "",
        lastName: userDetails?.last_name || "",
        userType: userDetails?.user_type || "",
      });
      setFormType("update");
    } else {
      setFormType("add");
    }
  }, [userDetails]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleModal = (payload) => {
    setOpenModal(true);
    setSelectedContent(payload);
  };

  const handleInputChange = (event) => {
    event.preventDefault();
    const { value, name } = event.target;
    const newField = { ...formValues, [name]: value };
    setFormValues(newField);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    const params = {
      data: {
        first_name: formValues?.firstName || "",
        last_name: formValues?.lastName || "",
        phone_number: "",
        email: user?.email,
        street: "",
        suburb: "",
        state: "",
        country: "",
        post_code: "",
        user_type: formValues?.userType || "",
      },
    };
    let response = {};
    if (formType === "add") {
      response = await createContactDetails(params, token);
    } else {
      response = await updateContactDetails(params, token);
    }
    setIsSubmitting(false);

    if ([204, 200].includes(response.status)) {
      setOpenModal(false);
      toast("Profile successfully updated");
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const params = {
      user_id: user?.sub,
    };
    const response = await verifyEmail(params);
    setIsSubmitting(false);

    if ([204, 200, 201].includes(response?.status)) {
      toast("Email Verification sent");
    } else {
      toast.error(response?.data?.message);
    }
    setOpenModal(false);
  };

  const renderContent = () => {
    let component = "";
    if (selectedContent === "profile") {
      component = (
        <CompleteProfile
          userDetails={userDetails}
          closeModal={() => setOpenModal(false)}
          handleInputChange={handleInputChange}
          formValues={formValues}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          user={user}
        />
      );
    } else if (selectedContent === "upload") {
      return <AddPaymentMethod />;
    } else if (selectedContent === "verifyEmail") {
      component = (
        <VerifyEmail
          formValues={user}
          isSubmitting={isSubmitting}
          handleVerifyEmail={handleVerifyEmail}
        />
      );
    }
    return component;
  };
  const getModalTitle = () => {
    let title = "";
    switch (selectedContent) {
      case "profile":
        title = "Complete Profle";
        break;
      case "upload":
        title = "Upload File";
        break;
      case "verifyEmail":
        title = "Verify Email Address";
        break;
      default:
        break;
    }
    return title;
  };
  const isProfileCompleted = () => {
    return (
      userDetails?.first_name &&
      userDetails?.last_name &&
      userDetails?.user_type
    );
  };

  return (
    <div data-testid="getting-started-page" className="getting-started-page">
      <header className="page-header" data-testid="page-header">
        <div
          data-testid="datafarming-logo"
          className="nav-logo"
          onClick={() => history.push("/")}
          aria-hidden
        >
          <img className="app-logo" src={appLogo} alt="alt" />
        </div>
        <label className="title">Welcome to DataFarming</label>
      </header>

      <div className="getting-started">
        <div className="title-container">
          <p className="title">Getting Started</p>
          <span
            aria-hidden
            onClick={() => history.push("/")}
            className="link-action"
            data-testid="title-action"
          >
            Proceed and finish later
          </span>
        </div>
        <div data-testid="steps-wrapper" className="content-wrapper">
          <div data-testid="profile-card" className="card">
            <div className="icon-container">
              <span className="icon-redirect" />
            </div>
            <p className="sub-title">Complete your profile</p>
            <p className="description">
              Configure your profile, contact details and payment methods.
            </p>
            {isProfileCompleted() ? (
              <Button
                label="Completed"
                color="green"
                type="outlined"
                block
                icon="check"
                onClick={() => toggleModal("profile")}
              />
            ) : (
              <Button
                label="Setup Profile"
                color="gray"
                type="outlined"
                block
                onClick={() => toggleModal("profile")}
              />
            )}
          </div>
          <div data-testid="upload-card" className="card">
            <div className="icon-container">
              <span className="icon-redirect" />
            </div>
            <p className="sub-title">Import your shape file</p>
            <p className="description">
              Never start empty. Import your farms and fields straight away.
            </p>
            <CreateNewFarmContainer
              buttonColor="gray"
              buttonType="outlined"
              buttonLabel="Upload File"
            />
          </div>
          <div data-testid="verify-card" className="card">
            <div className="icon-container">
              <span className="icon-verify" />
            </div>
            <p className="sub-title">Verify your email address</p>
            <p className="description">
              Secure your account by verifying your email address.
            </p>
            {user?.email_verified ? (
              <Button
                label="Completed"
                color="green"
                type="outlined"
                block
                icon="check"
                onClick={() => toggleModal("verifyEmail")}
              />
            ) : (
              <Button
                label="Verify Email Address"
                color="gray"
                type="outlined"
                block
                onClick={() => toggleModal("verifyEmail")}
              />
            )}
          </div>
          <div data-testid="learnmore-card" className="card more">
            <div className="icon-container">
              <span className="icon-guide" />
            </div>
            <p className="sub-title">Learn more with us</p>
            <p className="description">
              DataFarming provides help and support when in need.
            </p>
            <Button
              label="View Help & Support Guide"
              color="gray"
              type="outlined"
              onClick={() => toggleModal("viewHelp")}
            />
          </div>
        </div>
        {isProfileCompleted() && (
          <div className="card spiel">
            <span className="icon-redirect" />
            <p className="sub">Complete your profile. </p>
            <p className="description">Finish setup Profile.</p>
          </div>
        )}
        {user?.email_verified && (
          <div className="card spiel">
            <span className="icon-redirect" />
            <p className="sub">Verify Email Address </p>
            <p className="description">
              Successfully verified your email address.
            </p>
          </div>
        )}
      </div>
      <ModalContainer
        isOpen={openModal}
        onRequestClose={() => setOpenModal(false)}
        label={getModalTitle()}
        onClose={() => setOpenModal(false)}
      >
        {renderContent()}
      </ModalContainer>
    </div>
  );
};

GettingStartedContainer.propTypes = {};

export default GettingStartedContainer;
